import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import campaignService, {
  CampaignBulkRecipient,
  CampaignBulkStatusResponse,
  CampaignBulkValidationRow,
  CampaignBulkValidateResponse,
  CampaignInitiateResponse,
  CampaignWebhookCreateResponse,
  normalizePhoneNumber,
} from '../../services/campaignService';
import projectService, { ProjectMetadata } from '../../services/projectService';
import { VOICE_MODEL_OPTIONS } from '../../constants/clientVoice';
import {
  buildInitiateCampaignRequest,
  buildWebhookCampaignRequest,
  DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS,
  DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS,
  DEFAULT_REENGAGEMENT_DELAY_DAYS,
} from './campaignRequestBuilders';

interface FormErrors {
  templateName?: string;
  phoneNumber?: string;
  customerName?: string;
}

const DEFAULT_HEADER_IMAGE_PATH = '/assets/campaigns/Lanzamientos_Medellin_AyC.jpg';
const MIN_REENGAGEMENT_DELAY_DAYS = 0.000001;

const normalizeNoAnswerAttemptsInput = (value: string): number => {
  const parsed = Math.trunc(Number(value));
  return Number.isFinite(parsed) ? Math.min(10, Math.max(1, parsed)) : 1;
};

const normalizeReengagementDelayInput = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : MIN_REENGAGEMENT_DELAY_DAYS;
};

type LiveStatus = 'idle' | 'connecting' | 'connected' | 'fallback-polling' | 'disconnected';

type BulkRecipientChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';
type BulkRecipientStatusPresentation = {
  color: BulkRecipientChipColor;
  label: string;
  sx?: Record<string, string>;
};

const CampaignStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'completed') {
    return <Chip size="small" color="success" label="Completada" />;
  }
  if (normalized === 'failed') {
    return <Chip size="small" color="error" label="Fallida" />;
  }
  if (normalized === 'running') {
    return <Chip size="small" color="primary" label="En ejecución" />;
  }
  if (normalized === 'queued') {
    return <Chip size="small" color="warning" label="En cola" />;
  }
  if (normalized === 'cancelled') {
    return <Chip size="small" color="default" label="Cancelada" />;
  }
  return <Chip size="small" label={status || 'desconocido'} />;
};

export const getBulkRecipientStatusPresentation = (status: string): BulkRecipientStatusPresentation => {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'processing') {
    return {
      color: 'success',
      label: 'Procesado por WhatsApp',
      sx: {
        bgcolor: 'success.light',
        color: 'success.contrastText',
      },
    };
  }
  if (normalized === 'sent') {
    return { color: 'info', label: 'Enviado' };
  }
  if (normalized === 'delivered') {
    return { color: 'success', label: 'Recibido' };
  }
  if (normalized === 'read') {
    return { color: 'success', label: 'Leído' };
  }
  if (normalized === 'retry_scheduled') {
    return { color: 'warning', label: 'Reintento programado' };
  }
  if (normalized === 'retry_exhausted') {
    return { color: 'error', label: 'Reintentos agotados' };
  }
  if (normalized === 'failed') {
    return { color: 'error', label: 'Fallido' };
  }
  if (normalized === 'pending') {
    return { color: 'warning', label: 'Pendiente' };
  }
  if (normalized === 'invalid') {
    return { color: 'default', label: 'Inválido' };
  }

  return { color: 'default', label: status || 'Desconocido' };
};

const BulkRecipientStatusChip: React.FC<{ status: string }> = ({ status }) => {
  const presentation = getBulkRecipientStatusPresentation(status);
  return (
    <Chip
      size="small"
      color={presentation.color}
      label={presentation.label}
      sx={presentation.sx}
    />
  );
};

const getProjectOptionLabel = (project: ProjectMetadata): string =>
  project.project_name || project.project_code;

const InitiateCampaign: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Single-send state
  const [templateName, setTemplateName] = useState('prueba_sara');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [skipIdentityValidation, setSkipIdentityValidation] = useState(false);
  const [resetConversationMemory, setResetConversationMemory] = useState(false);
  const [scheduleReengagement, setScheduleReengagement] = useState(true);
  const [reengagementDelayBusinessDays, setReengagementDelayBusinessDays] = useState(DEFAULT_REENGAGEMENT_DELAY_DAYS);
  const [noAnswerReengagementAttempts, setNoAnswerReengagementAttempts] = useState(DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS);
  const [noAnswerReengagementIncrementBusinessDays, setNoAnswerReengagementIncrementBusinessDays] = useState(DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS);
  const [headerMediaType, setHeaderMediaType] = useState<'image' | 'video' | null>(null);
  const [headerImageMediaId, setHeaderImageMediaId] = useState<string | null>(null);
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successResponse, setSuccessResponse] = useState<CampaignInitiateResponse | null>(null);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectMetadata | null>(null);

  // Bulk-send state
  const [bulkTemplateName, setBulkTemplateName] = useState('prueba_sara');
  const [bulkSkipIdentityValidation, setBulkSkipIdentityValidation] = useState(false);
  const [bulkResetConversationMemory, setBulkResetConversationMemory] = useState(false);
  const [bulkScheduleReengagement, setBulkScheduleReengagement] = useState(true);
  const [bulkReengagementDelayBusinessDays, setBulkReengagementDelayBusinessDays] = useState(DEFAULT_REENGAGEMENT_DELAY_DAYS);
  const [bulkNoAnswerReengagementAttempts, setBulkNoAnswerReengagementAttempts] = useState(DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS);
  const [bulkNoAnswerReengagementIncrementBusinessDays, setBulkNoAnswerReengagementIncrementBusinessDays] = useState(DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS);
  const [bulkHeaderMediaType, setBulkHeaderMediaType] = useState<'image' | 'video' | null>(null);
  const [bulkHeaderImageMediaId, setBulkHeaderImageMediaId] = useState<string | null>(null);
  const [bulkHeaderImageUrl, setBulkHeaderImageUrl] = useState<string | null>(null);
  const [bulkUploadingImage, setBulkUploadingImage] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkErrorMessage, setBulkErrorMessage] = useState<string | null>(null);
  const [bulkSuccessMessage, setBulkSuccessMessage] = useState<string | null>(null);
  const [validatingBulk, setValidatingBulk] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkValidation, setBulkValidation] = useState<CampaignBulkValidateResponse | null>(null);
  const [bulkCampaignId, setBulkCampaignId] = useState<string | null>(null);
  const [bulkStatus, setBulkStatus] = useState<CampaignBulkStatusResponse | null>(null);
  const [bulkRecipients, setBulkRecipients] = useState<CampaignBulkRecipient[]>([]);
  const [bulkRecipientsTotal, setBulkRecipientsTotal] = useState(0);
  const [bulkRecipientsLoading, setBulkRecipientsLoading] = useState(false);
  const [recipientPage, setRecipientPage] = useState(0);
  const [recipientPageSize, setRecipientPageSize] = useState(25);
  const [liveStatus, setLiveStatus] = useState<LiveStatus>('idle');
  const [bulkSelectedProject, setBulkSelectedProject] = useState<ProjectMetadata | null>(null);

  // Webhook campaign state
  const [webhookCampaignName, setWebhookCampaignName] = useState('Webhook HubSpot');
  const [webhookVoiceModelSelection, setWebhookVoiceModelSelection] = useState('');
  const [webhookTemplateName, setWebhookTemplateName] = useState('prueba_sara');
  const [webhookSkipIdentityValidation, setWebhookSkipIdentityValidation] = useState(false);
  const [webhookScheduleReengagement, setWebhookScheduleReengagement] = useState(true);
  const [webhookReengagementDelayBusinessDays, setWebhookReengagementDelayBusinessDays] = useState(DEFAULT_REENGAGEMENT_DELAY_DAYS);
  const [webhookNoAnswerReengagementAttempts, setWebhookNoAnswerReengagementAttempts] = useState(DEFAULT_NO_ANSWER_REENGAGEMENT_ATTEMPTS);
  const [webhookNoAnswerReengagementIncrementBusinessDays, setWebhookNoAnswerReengagementIncrementBusinessDays] = useState(DEFAULT_NO_ANSWER_REENGAGEMENT_INCREMENT_DAYS);
  const [webhookHeaderMediaType, setWebhookHeaderMediaType] = useState<'image' | 'video' | null>(null);
  const [webhookHeaderImageMediaId, setWebhookHeaderImageMediaId] = useState<string | null>(null);
  const [webhookHeaderImageUrl, setWebhookHeaderImageUrl] = useState<string | null>(null);
  const [webhookRuntimeVariablesInput, setWebhookRuntimeVariablesInput] = useState('');
  const [webhookSignatureSecret, setWebhookSignatureSecret] = useState('');
  const [webhookUploadingImage, setWebhookUploadingImage] = useState(false);
  const [webhookSubmitting, setWebhookSubmitting] = useState(false);
  const [webhookErrorMessage, setWebhookErrorMessage] = useState<string | null>(null);
  const [webhookSuccessResponse, setWebhookSuccessResponse] = useState<CampaignWebhookCreateResponse | null>(null);
  const [webhookSelectedProject, setWebhookSelectedProject] = useState<ProjectMetadata | null>(null);

  const [projectOptions, setProjectOptions] = useState<ProjectMetadata[]>([]);
  const [projectSearchInput, setProjectSearchInput] = useState('');
  const [projectsLoading, setProjectsLoading] = useState(false);

  const pollTimerRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const validationErrors = useMemo<FormErrors>(() => {
    const errors: FormErrors = {};
    const normalizedPhone = normalizePhoneNumber(phoneNumber);

    if (!templateName.trim()) {
      errors.templateName = 'La plantilla es obligatoria.';
    }

    if (!customerName.trim()) {
      errors.customerName = 'El nombre es obligatorio.';
    }

    if (!normalizedPhone) {
      errors.phoneNumber = 'El número de teléfono es obligatorio.';
    } else if (normalizedPhone.length < 10 || normalizedPhone.length > 15) {
      errors.phoneNumber = 'El número debe tener entre 10 y 15 dígitos.';
    }

    return errors;
  }, [templateName, phoneNumber, customerName]);

  const hasErrors = Object.keys(validationErrors).length > 0;

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setProjectsLoading(true);
    projectService
      .getProjects(0, 25, true, projectSearchInput.trim() || undefined)
      .then((response) => {
        if (isMounted) {
          setProjectOptions(response.projects || []);
        }
      })
      .catch((error) => {
        console.error('Error loading campaign projects:', error);
      })
      .finally(() => {
        if (isMounted) {
          setProjectsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [projectSearchInput]);

  const clearProgressTracking = () => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const renderProjectSelector = (
    value: ProjectMetadata | null,
    onChange: (project: ProjectMetadata | null) => void,
    disabled = false
  ) => (
    <Autocomplete
      options={projectOptions}
      value={value}
      loading={projectsLoading}
      disabled={disabled}
      getOptionLabel={getProjectOptionLabel}
      isOptionEqualToValue={(option, selected) => option.project_code === selected.project_code}
      filterOptions={(options) => options}
      onInputChange={(_, nextInput) => setProjectSearchInput(nextInput)}
      onChange={(_, nextProject) => onChange(nextProject)}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Proyecto de interés (opcional)"
          helperText="Se guardará como contexto inicial de la conversación."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {projectsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ maxWidth: 520 }}
    />
  );

  const fetchCampaignStatus = async (campaignId: string): Promise<CampaignBulkStatusResponse | null> => {
    try {
      const status = await campaignService.getBulkCampaignStatus(campaignId);
      setBulkStatus(status);
      return status;
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible consultar el estado de la campaña.';
      setBulkErrorMessage(String(backendMessage));
      return null;
    }
  };

  const fetchCampaignRecipients = async (campaignId: string, page: number, pageSize: number) => {
    setBulkRecipientsLoading(true);
    try {
      const response = await campaignService.getBulkCampaignRecipients(campaignId, {
        skip: page * pageSize,
        limit: pageSize,
      });
      setBulkRecipients(response.recipients || []);
      setBulkRecipientsTotal(response.total_count || 0);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible consultar el detalle de destinatarios.';
      setBulkErrorMessage(String(backendMessage));
    } finally {
      setBulkRecipientsLoading(false);
    }
  };

  const startPollingFallback = (campaignId: string) => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
    }
    setLiveStatus('fallback-polling');
    pollTimerRef.current = window.setInterval(async () => {
      const status = await fetchCampaignStatus(campaignId);
      if (status) {
        await fetchCampaignRecipients(campaignId, recipientPage, recipientPageSize);
        if (['completed', 'failed', 'cancelled'].includes(status.status)) {
          if (pollTimerRef.current) {
            window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          setLiveStatus('disconnected');
        }
      }
    }, 3000);
  };

  const streamCampaignProgress = async (campaignId: string) => {
    clearProgressTracking();
    setLiveStatus('connecting');

    const apiBase = process.env.REACT_APP_API_URL || '/api';
    const streamUrl = `${apiBase}/campaigns/bulk/${campaignId}/stream?interval_ms=1200`;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const readSseStream = async (
      body: ReadableStream<Uint8Array>,
      onEvent: (eventName: string, data: string) => Promise<void>
    ) => {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        let idx = buffer.indexOf('\n\n');
        while (idx !== -1) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          idx = buffer.indexOf('\n\n');

          const lines = rawEvent.split(/\r?\n/);
          let eventName = 'message';
          const dataLines: string[] = [];

          for (const line of lines) {
            if (!line || line.startsWith(':')) {
              continue;
            }
            if (line.startsWith('event:')) {
              eventName = line.slice('event:'.length).trim() || 'message';
              continue;
            }
            if (line.startsWith('data:')) {
              dataLines.push(line.slice('data:'.length).trimStart());
            }
          }

          const data = dataLines.join('\n');
          if (data) {
            await onEvent(eventName, data);
          }
        }
      }
    };

    try {
      const token = localStorage.getItem('access_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(streamUrl, {
        method: 'GET',
        headers,
        signal: abortController.signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`Campaign status stream failed (${response.status})`);
      }

      setLiveStatus('connected');
      await readSseStream(response.body, async (eventName, data) => {
        if (eventName === 'campaign-progress') {
          try {
            const payload = JSON.parse(data) as CampaignBulkStatusResponse;
            setBulkStatus(payload);
            await fetchCampaignRecipients(campaignId, recipientPage, recipientPageSize);
          } catch (err) {
            console.error('Failed to parse campaign-progress payload', err);
          }
        }
      });

      const status = await fetchCampaignStatus(campaignId);
      if (status && !['completed', 'failed', 'cancelled'].includes(status.status)) {
        startPollingFallback(campaignId);
      } else {
        setLiveStatus('disconnected');
      }
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }
      console.warn('Campaign status stream disconnected; fallback to polling', error);
      startPollingFallback(campaignId);
    }
  };

  useEffect(() => {
    if (!bulkCampaignId) {
      return;
    }
    streamCampaignProgress(bulkCampaignId);

    return () => {
      clearProgressTracking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkCampaignId]);

  useEffect(() => {
    if (!bulkCampaignId) {
      return;
    }
    fetchCampaignRecipients(bulkCampaignId, recipientPage, recipientPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkCampaignId, recipientPage, recipientPageSize]);

  const handleSubmitSingle = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setWasSubmitted(true);
    setErrorMessage(null);
    setSuccessResponse(null);

    if (hasErrors) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await campaignService.initiateCampaign(
        buildInitiateCampaignRequest({
          templateName,
          phoneNumber,
          customerName,
          skipIdentityValidation,
          resetConversationMemory,
          scheduleReengagement,
          reengagementDelayBusinessDays,
          noAnswerReengagementAttempts,
          noAnswerReengagementIncrementBusinessDays,
          headerMediaType,
          headerImageMediaId,
          headerImageUrl,
          projectInterest: selectedProject?.project_code || null,
        })
      );
      setSuccessResponse(response);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible enviar la plantilla de WhatsApp.';
      setErrorMessage(String(backendMessage));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSingleImageSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setErrorMessage(null);
    setSuccessResponse(null);
    setUploadingImage(true);
    try {
      const upload = await campaignService.uploadCampaignHeaderMedia(file);
      setHeaderMediaType(upload.media_type);
      setHeaderImageMediaId(upload.media_id);
      setHeaderImageUrl(upload.media_url);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible cargar la media del encabezado.';
      setErrorMessage(String(backendMessage));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleBulkImageSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setBulkErrorMessage(null);
    setBulkUploadingImage(true);
    try {
      const upload = await campaignService.uploadCampaignHeaderMedia(file);
      setBulkHeaderMediaType(upload.media_type);
      setBulkHeaderImageMediaId(upload.media_id);
      setBulkHeaderImageUrl(upload.media_url);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible cargar la media del encabezado masivo.';
      setBulkErrorMessage(String(backendMessage));
    } finally {
      setBulkUploadingImage(false);
    }
  };

  const handleWebhookImageSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setWebhookErrorMessage(null);
    setWebhookUploadingImage(true);
    try {
      const upload = await campaignService.uploadCampaignHeaderMedia(file);
      setWebhookHeaderMediaType(upload.media_type);
      setWebhookHeaderImageMediaId(upload.media_id);
      setWebhookHeaderImageUrl(upload.media_url);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible cargar la media del encabezado del webhook.';
      setWebhookErrorMessage(String(backendMessage));
    } finally {
      setWebhookUploadingImage(false);
    }
  };

  const handleCreateWebhookCampaign = async () => {
    if (!webhookCampaignName.trim()) {
      setWebhookErrorMessage('El nombre de la campaña es obligatorio.');
      return;
    }
    if (!webhookTemplateName.trim()) {
      setWebhookErrorMessage('La plantilla WhatsApp es obligatoria.');
      return;
    }

    setWebhookSubmitting(true);
    setWebhookErrorMessage(null);
    setWebhookSuccessResponse(null);
    try {
      const response = await campaignService.createWebhookCampaign(
        buildWebhookCampaignRequest({
          campaignName: webhookCampaignName,
          voiceModelSelection: webhookVoiceModelSelection,
          templateName: webhookTemplateName,
          skipIdentityValidation: webhookSkipIdentityValidation,
          runtimeVariableNamesInput: webhookRuntimeVariablesInput,
          headerMediaType: webhookHeaderMediaType,
          headerImageMediaId: webhookHeaderImageMediaId,
          headerImageUrl: webhookHeaderImageUrl,
          hubspotSignatureSecret: webhookSignatureSecret,
          scheduleReengagement: webhookScheduleReengagement,
          reengagementDelayBusinessDays: webhookReengagementDelayBusinessDays,
          noAnswerReengagementAttempts: webhookNoAnswerReengagementAttempts,
          noAnswerReengagementIncrementBusinessDays: webhookNoAnswerReengagementIncrementBusinessDays,
          projectInterest: webhookSelectedProject?.project_code || null,
        })
      );
      setWebhookSuccessResponse(response);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible crear la campaña webhook.';
      setWebhookErrorMessage(String(backendMessage));
    } finally {
      setWebhookSubmitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setBulkErrorMessage(null);
    try {
      const blob = await campaignService.downloadBulkTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'campaign_bulk_template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible descargar el archivo ejemplo.';
      setBulkErrorMessage(String(backendMessage));
    }
  };

  const handleValidateBulkFile = async () => {
    if (!bulkFile) {
      setBulkErrorMessage('Selecciona un archivo .xlsx para validar.');
      return;
    }
    if (!bulkTemplateName.trim()) {
      setBulkErrorMessage('La plantilla WhatsApp es obligatoria para validar.');
      return;
    }

    setValidatingBulk(true);
    setBulkErrorMessage(null);
    setBulkSuccessMessage(null);
    setBulkValidation(null);
    setBulkCampaignId(null);
    setBulkStatus(null);
    setBulkRecipients([]);
    setBulkRecipientsTotal(0);
    clearProgressTracking();
    setLiveStatus('idle');

    try {
      const formData = new FormData();
      formData.append('template_name', bulkTemplateName.trim());
      formData.append('language_code', 'es_CO');
      formData.append('skip_identity_validation', bulkSkipIdentityValidation ? 'true' : 'false');
      formData.append('reset_conversation_memory', bulkResetConversationMemory ? 'true' : 'false');
      formData.append('schedule_reengagement', bulkScheduleReengagement ? 'true' : 'false');
      if (bulkScheduleReengagement) {
        formData.append('reengagement_delay_business_days', String(bulkReengagementDelayBusinessDays));
        formData.append('no_answer_reengagement_attempts', String(bulkNoAnswerReengagementAttempts));
        formData.append('no_answer_reengagement_increment_business_days', String(bulkNoAnswerReengagementIncrementBusinessDays));
      }
      if (bulkSelectedProject?.project_code) {
        formData.append('project_interest', bulkSelectedProject.project_code);
      }
      if (bulkHeaderMediaType) {
        formData.append('header_media_type', bulkHeaderMediaType);
      }
      if (bulkHeaderImageMediaId) {
        formData.append('header_media_id', bulkHeaderImageMediaId);
        formData.append('header_image_media_id', bulkHeaderImageMediaId);
      }
      if (bulkHeaderImageUrl) {
        formData.append('header_media_url', bulkHeaderImageUrl);
        formData.append('header_image_url', bulkHeaderImageUrl);
      }
      formData.append('file', bulkFile);

      const response = await campaignService.validateBulkCampaign(formData);
      setBulkValidation(response);
      setBulkSuccessMessage(
        `Validación completada: ${response.valid_rows} registros válidos de ${response.total_rows}.`
      );
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible validar el archivo.';
      setBulkErrorMessage(String(backendMessage));
    } finally {
      setValidatingBulk(false);
    }
  };

  const handleStartBulkSend = async () => {
    if (!bulkValidation?.validation_token) {
      setBulkErrorMessage('Valida el archivo antes de iniciar el envío.');
      return;
    }

    setSendingBulk(true);
    setBulkErrorMessage(null);
    setBulkSuccessMessage(null);
    try {
      const response = await campaignService.sendBulkCampaign(bulkValidation.validation_token);
      setBulkCampaignId(response.campaign_id);
      setRecipientPage(0);
      setBulkSuccessMessage(
        `Campaña en cola (${response.campaign_id}). Se enviarán ${response.valid_rows} destinatarios válidos.`
      );
      await fetchCampaignStatus(response.campaign_id);
      await fetchCampaignRecipients(response.campaign_id, 0, recipientPageSize);
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'No fue posible encolar la campaña masiva.';
      setBulkErrorMessage(String(backendMessage));
    } finally {
      setSendingBulk(false);
    }
  };

  const effectiveSinglePreviewUrl = headerImageUrl || DEFAULT_HEADER_IMAGE_PATH;
  const effectiveBulkPreviewUrl = bulkHeaderImageUrl || DEFAULT_HEADER_IMAGE_PATH;
  const effectiveWebhookPreviewUrl = webhookHeaderImageUrl || DEFAULT_HEADER_IMAGE_PATH;

  const validationColumns: GridColDef[] = [
    { field: 'row_number', headerName: 'Fila', width: 90 },
    { field: 'phone_number', headerName: 'Número normalizado', minWidth: 180, flex: 0.8 },
    {
      field: 'is_valid',
      headerName: 'Estado',
      width: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          color={params.value ? 'success' : 'error'}
          label={params.value ? 'Válido' : 'Inválido'}
        />
      ),
    },
    {
      field: 'errors',
      headerName: 'Errores',
      flex: 1.2,
      minWidth: 260,
      valueGetter: (params) => (params.row.errors || []).join(' | '),
    },
    {
      field: 'variables',
      headerName: 'Variables',
      flex: 1.5,
      minWidth: 280,
      valueGetter: (params) => {
        const variables = params.row.variables || {};
        const entries = Object.entries(variables).map(([key, value]) => `${key}: ${value}`);
        return entries.join(' | ');
      },
    },
  ];

  const recipientColumns: GridColDef[] = [
    { field: 'row_number', headerName: 'Fila', width: 90 },
    { field: 'phone_number', headerName: 'Número', minWidth: 170, flex: 0.7 },
    {
      field: 'status',
      headerName: 'Estado',
      minWidth: 140,
      flex: 0.6,
      renderCell: (params) => <BulkRecipientStatusChip status={String(params.value || '')} />,
    },
    { field: 'whatsapp_message_id', headerName: 'WhatsApp ID', minWidth: 220, flex: 1 },
    {
      field: 'warnings',
      headerName: 'Advertencias',
      minWidth: 300,
      flex: 1.2,
      valueGetter: (params) => (params.row.warnings || []).join(' '),
    },
    { field: 'error_message', headerName: 'Error', minWidth: 260, flex: 1.1 },
  ];

  return (
    <Box className="fade-in">
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Iniciar Campaña
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Envía plantillas de WhatsApp de forma individual o masiva, o crea campañas webhook de larga vida.
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 2 }}>
          <Tab label="Envío Individual" />
          <Tab label="Envío Masivo (Excel)" />
          <Tab label="Webhooks" />
        </Tabs>

        {activeTab === 0 && (
          <Stack component="form" spacing={2.5} onSubmit={handleSubmitSingle} noValidate sx={{ maxWidth: 680 }}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            {successResponse && (
              <Stack spacing={1.5}>
                <Alert severity="success">
                  Plantilla enviada correctamente a {successResponse.to}
                  {successResponse.message_id ? ` (ID: ${successResponse.message_id})` : ''}.
                </Alert>

                {!!successResponse.warnings?.length && (
                  <Alert severity="warning">
                    {successResponse.warnings.join(' ')}
                  </Alert>
                )}

                {successResponse.conversation_id && (
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const target = encodeURIComponent(successResponse.conversation_id as string);
                        navigate(`/conversations?conversationId=${target}`);
                      }}
                    >
                      Ver conversación
                    </Button>
                  </Box>
                )}
              </Stack>
            )}

            <TextField
              label="Plantilla WhatsApp"
              name="templateName"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
              fullWidth
              error={wasSubmitted && !!validationErrors.templateName}
              helperText={wasSubmitted ? validationErrors.templateName : 'Valor inicial: prueba_sara'}
            />

            <TextField
              label="Número de teléfono"
              name="phoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
              fullWidth
              placeholder="+57 300 123 4567"
              error={wasSubmitted && !!validationErrors.phoneNumber}
              helperText={
                wasSubmitted
                  ? validationErrors.phoneNumber
                  : 'Se normaliza automáticamente a formato E.164 (solo dígitos).'
              }
            />

            <TextField
              label="Nombre"
              name="customerName"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              required
              fullWidth
              error={wasSubmitted && !!validationErrors.customerName}
              helperText={
                wasSubmitted
                  ? validationErrors.customerName
                  : 'Se envía como variable `nombre_cliente` (parámetro body #1).'
              }
            />

            {renderProjectSelector(selectedProject, setSelectedProject, submitting)}

            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={skipIdentityValidation}
                    onChange={(event) => setSkipIdentityValidation(event.target.checked)}
                    color="primary"
                  />
                )}
                label="Omitir validación de identidad"
              />
              <Typography variant="body2" color="text.secondary">
                Solo aplica si el número ya existe en HubSpot o en clientes locales. Si no existe, el contacto seguirá el flujo de registro actual.
              </Typography>
            </Box>

            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={resetConversationMemory}
                    onChange={(event) => setResetConversationMemory(event.target.checked)}
                    color="primary"
                  />
                )}
                label="Borrar memoria de IA antes de enviar"
              />
              <Typography variant="body2" color="text.secondary">
                Cierra la conversación anterior y borra la memoria aprendida antes de contactar a Meta. Conserva el historial visible, la identidad y los consentimientos.
              </Typography>
            </Box>

            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={scheduleReengagement}
                    onChange={(event) => setScheduleReengagement(event.target.checked)}
                    color="primary"
                  />
                )}
                label="Programar reenganche"
              />
              <TextField
                label="Primer reenganche (días hábiles)"
                type="number"
                value={reengagementDelayBusinessDays}
                onChange={(event) => setReengagementDelayBusinessDays(normalizeReengagementDelayInput(event.target.value))}
                inputProps={{ min: MIN_REENGAGEMENT_DELAY_DAYS, step: 'any' }}
                sx={{ maxWidth: 260, display: 'block', mt: 1 }}
                disabled={!scheduleReengagement}
                helperText={
                  scheduleReengagement
                    ? 'Enteros usan días hábiles; decimales usan tiempo exacto para pruebas.'
                    : 'No se programará reenganche para esta campaña.'
                }
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
                <TextField
                  label="Reenganches sin respuesta"
                  type="number"
                  value={noAnswerReengagementAttempts}
                  onChange={(event) => setNoAnswerReengagementAttempts(normalizeNoAnswerAttemptsInput(event.target.value))}
                  inputProps={{ min: 1, max: 10, step: 1 }}
                  disabled={!scheduleReengagement}
                  helperText="Incluye el intento final."
                />
                <TextField
                  label="Incremento por intento (días hábiles)"
                  type="number"
                  value={noAnswerReengagementIncrementBusinessDays}
                  onChange={(event) => setNoAnswerReengagementIncrementBusinessDays(normalizeReengagementDelayInput(event.target.value))}
                  inputProps={{ min: MIN_REENGAGEMENT_DELAY_DAYS, step: 'any' }}
                  disabled={!scheduleReengagement}
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Media de Encabezado
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Por defecto se usa <code>Lanzamientos_Medellin_AyC.jpg</code>. Puedes subir imagen o video para este envío.
              </Typography>

              {headerMediaType === 'video' && headerImageUrl ? (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Vista previa video (encabezado): {headerImageUrl}
                  </Typography>
                  <Box
                    component="video"
                    src={headerImageUrl}
                    controls
                    sx={{
                      width: '100%',
                      maxWidth: 460,
                      maxHeight: 220,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                </Box>
              ) : (
                <Box
                  component="img"
                  src={effectiveSinglePreviewUrl}
                  alt="Vista previa de imagen de encabezado"
                  sx={{
                    width: '100%',
                    maxWidth: 460,
                    maxHeight: 220,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 1.5,
                  }}
                />
              )}

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button component="label" variant="outlined" disabled={uploadingImage || submitting}>
                  {uploadingImage ? 'Cargando media...' : 'Subir media'}
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp,video/mp4"
                    onChange={handleSingleImageSelection}
                    data-testid="campaign-header-upload-input"
                  />
                </Button>

                {uploadingImage && <CircularProgress size={20} />}

                {headerImageUrl && (
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => {
                      setHeaderImageUrl(null);
                      setHeaderImageMediaId(null);
                      setHeaderMediaType(null);
                    }}
                    disabled={uploadingImage || submitting}
                  >
                    Usar predeterminada
                  </Button>
                )}
              </Stack>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button type="submit" variant="contained" disabled={submitting || uploadingImage}>
                {submitting ? 'Enviando...' : 'Enviar plantilla'}
              </Button>
            </Box>
          </Stack>
        )}

        {activeTab === 1 && (
          <Stack spacing={2.5}>
            {bulkErrorMessage && <Alert severity="error">{bulkErrorMessage}</Alert>}
            {bulkSuccessMessage && <Alert severity="success">{bulkSuccessMessage}</Alert>}

            <TextField
              label="Plantilla WhatsApp"
              value={bulkTemplateName}
              onChange={(e) => setBulkTemplateName(e.target.value)}
              fullWidth
              sx={{ maxWidth: 520 }}
              helperText="Se usará para validar variables contra la API de plantillas de WhatsApp."
            />

            {renderProjectSelector(
              bulkSelectedProject,
              setBulkSelectedProject,
              validatingBulk || sendingBulk
            )}

            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={bulkSkipIdentityValidation}
                    onChange={(event) => setBulkSkipIdentityValidation(event.target.checked)}
                    color="primary"
                  />
                )}
                label="Omitir validación de identidad"
              />
              <Typography variant="body2" color="text.secondary">
                Solo aplica si el número ya existe en HubSpot o en clientes locales. Si no existe, el contacto seguirá el flujo de registro actual.
              </Typography>
            </Box>

            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={bulkResetConversationMemory}
                    onChange={(event) => setBulkResetConversationMemory(event.target.checked)}
                    color="primary"
                  />
                )}
                label="Borrar memoria de IA antes de enviar"
              />
              <Typography variant="body2" color="text.secondary">
                Para cada contacto, cierra la conversación anterior y borra la memoria aprendida antes del primer envío. El historial visible, la identidad y los consentimientos se conservan.
              </Typography>
            </Box>

            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={bulkScheduleReengagement}
                    onChange={(event) => setBulkScheduleReengagement(event.target.checked)}
                    color="primary"
                  />
                )}
                label="Programar reenganche"
              />
              <TextField
                label="Primer reenganche (días hábiles)"
                type="number"
                value={bulkReengagementDelayBusinessDays}
                onChange={(event) => setBulkReengagementDelayBusinessDays(normalizeReengagementDelayInput(event.target.value))}
                inputProps={{ min: MIN_REENGAGEMENT_DELAY_DAYS, step: 'any' }}
                sx={{ maxWidth: 260, display: 'block', mt: 1 }}
                disabled={!bulkScheduleReengagement}
                helperText={
                  bulkScheduleReengagement
                    ? 'Enteros usan días hábiles; decimales usan tiempo exacto para pruebas.'
                    : 'No se programará reenganche para esta campaña.'
                }
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
                <TextField
                  label="Reenganches sin respuesta"
                  type="number"
                  value={bulkNoAnswerReengagementAttempts}
                  onChange={(event) => setBulkNoAnswerReengagementAttempts(normalizeNoAnswerAttemptsInput(event.target.value))}
                  inputProps={{ min: 1, max: 10, step: 1 }}
                  disabled={!bulkScheduleReengagement}
                  helperText="Incluye el intento final."
                />
                <TextField
                  label="Incremento por intento (días hábiles)"
                  type="number"
                  value={bulkNoAnswerReengagementIncrementBusinessDays}
                  onChange={(event) => setBulkNoAnswerReengagementIncrementBusinessDays(normalizeReengagementDelayInput(event.target.value))}
                  inputProps={{ min: MIN_REENGAGEMENT_DELAY_DAYS, step: 'any' }}
                  disabled={!bulkScheduleReengagement}
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Media de Encabezado
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Puedes usar la predeterminada o subir imagen/video para toda la campaña masiva.
              </Typography>

              {bulkHeaderMediaType === 'video' && bulkHeaderImageUrl ? (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Vista previa video (encabezado): {bulkHeaderImageUrl}
                  </Typography>
                  <Box
                    component="video"
                    src={bulkHeaderImageUrl}
                    controls
                    sx={{
                      width: '100%',
                      maxWidth: 460,
                      maxHeight: 220,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                </Box>
              ) : (
                <Box
                  component="img"
                  src={effectiveBulkPreviewUrl}
                  alt="Vista previa imagen masiva"
                  sx={{
                    width: '100%',
                    maxWidth: 460,
                    maxHeight: 220,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 1.5,
                  }}
                />
              )}

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button component="label" variant="outlined" disabled={bulkUploadingImage || validatingBulk || sendingBulk}>
                  {bulkUploadingImage ? 'Cargando media...' : 'Subir media'}
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp,video/mp4"
                    onChange={handleBulkImageSelection}
                  />
                </Button>
                {bulkUploadingImage && <CircularProgress size={20} />}
                {bulkHeaderImageUrl && (
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => {
                      setBulkHeaderImageUrl(null);
                      setBulkHeaderImageMediaId(null);
                      setBulkHeaderMediaType(null);
                    }}
                    disabled={bulkUploadingImage || validatingBulk || sendingBulk}
                  >
                    Usar predeterminada
                  </Button>
                )}
              </Stack>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button variant="outlined" onClick={handleDownloadTemplate}>
                Descargar ejemplo Excel
              </Button>
              <Button component="label" variant="outlined" disabled={validatingBulk || sendingBulk}>
                Seleccionar archivo .xlsx
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(event) => {
                    const selected = event.target.files?.[0] || null;
                    event.target.value = '';
                    setBulkFile(selected);
                    if (selected) {
                      setBulkErrorMessage(null);
                    }
                  }}
                />
              </Button>
              <Button
                variant="contained"
                onClick={handleValidateBulkFile}
                disabled={!bulkFile || validatingBulk || sendingBulk}
              >
                {validatingBulk ? 'Validando...' : 'Validar archivo'}
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {bulkFile ? `Archivo seleccionado: ${bulkFile.name}` : 'No hay archivo seleccionado.'}
            </Typography>

            {bulkValidation && (
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                  <Chip color="info" label={`Total: ${bulkValidation.total_rows}`} />
                  <Chip color="success" label={`Válidos: ${bulkValidation.valid_rows}`} />
                  <Chip color="error" label={`Inválidos: ${bulkValidation.invalid_rows}`} />
                  <Chip label={`Modo variables: ${bulkValidation.mode}`} />
                </Stack>

                <Box sx={{ height: 420, width: '100%' }}>
                  <DataGrid
                    rows={bulkValidation.rows.map((row: CampaignBulkValidationRow) => ({
                      id: row.row_number,
                      ...row,
                    }))}
                    columns={validationColumns}
                    disableSelectionOnClick
                    pageSize={25}
                    rowsPerPageOptions={[25, 50, 100]}
                    pagination
                  />
                </Box>

                <Box>
                  <Button
                    variant="contained"
                    onClick={handleStartBulkSend}
                    disabled={sendingBulk || validatingBulk || bulkValidation.valid_rows <= 0}
                  >
                    {sendingBulk ? 'Encolando campaña...' : 'Enviar campaña masiva'}
                  </Button>
                </Box>
              </Stack>
            )}

            {bulkStatus && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <Typography variant="h6" sx={{ mr: 1 }}>
                      Progreso Campaña
                    </Typography>
                    <CampaignStatusChip status={bulkStatus.status} />
                    <Chip
                      size="small"
                      label={
                        liveStatus === 'connected'
                          ? 'Actualización en vivo'
                          : liveStatus === 'fallback-polling'
                          ? 'Fallback por sondeo'
                          : liveStatus === 'connecting'
                          ? 'Conectando...'
                          : 'Sin conexión'
                      }
                    />
                    <Button
                      variant="text"
                      size="small"
                      onClick={async () => {
                        if (bulkCampaignId) {
                          await fetchCampaignStatus(bulkCampaignId);
                          await fetchCampaignRecipients(bulkCampaignId, recipientPage, recipientPageSize);
                        }
                      }}
                    >
                      Actualizar
                    </Button>
                  </Stack>

                  <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, bulkStatus.progress_percent || 0))} />
                  <Typography variant="body2" color="text.secondary">
                    {bulkStatus.progress_percent.toFixed(2)}% completado ({bulkStatus.processed_rows}/{bulkStatus.valid_rows} válidos procesados)
                  </Typography>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <Chip color="success" label={`Enviados: ${bulkStatus.sent_rows}`} />
                    <Chip color="error" label={`Fallidos: ${bulkStatus.failed_rows}`} />
                    <Chip color="warning" label={`Pendientes: ${bulkStatus.pending_rows}`} />
                    <Chip label={`Omitidos: ${bulkStatus.skipped_rows}`} />
                  </Stack>

                  {bulkStatus.error_message && <Alert severity="error">{bulkStatus.error_message}</Alert>}
                </Stack>
              </Paper>
            )}

            {bulkCampaignId && (
              <Box sx={{ height: 420, width: '100%' }}>
                <DataGrid
                  rows={bulkRecipients}
                  columns={recipientColumns}
                  rowCount={bulkRecipientsTotal}
                  loading={bulkRecipientsLoading}
                  pageSize={recipientPageSize}
                  rowsPerPageOptions={[25, 50, 100]}
                  paginationMode="server"
                  page={recipientPage}
                  onPageChange={(newPage) => setRecipientPage(newPage)}
                  onPageSizeChange={(newPageSize) => {
                    setRecipientPageSize(newPageSize);
                    setRecipientPage(0);
                  }}
                  disableSelectionOnClick
                  pagination
                />
              </Box>
            )}
          </Stack>
        )}

        {activeTab === 2 && (
          <Stack spacing={2.5} sx={{ maxWidth: 760 }}>
            {webhookErrorMessage && <Alert severity="error">{webhookErrorMessage}</Alert>}

            {webhookSuccessResponse && (
              <Stack spacing={1.5}>
                <Alert severity="success">
                  Webhook creado correctamente para la campaña {webhookSuccessResponse.campaign_name}.
                </Alert>

                <TextField
                  label="Endpoint del webhook"
                  value={webhookSuccessResponse.webhook_url}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />

                <TextField
                  label="Bearer token (se muestra una sola vez)"
                  value={webhookSuccessResponse.bearer_token}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText={`Hint almacenado: ${webhookSuccessResponse.bearer_token_hint}`}
                />
              </Stack>
            )}

            <TextField
              label="Nombre de la campaña"
              value={webhookCampaignName}
              onChange={(event) => setWebhookCampaignName(event.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Plantilla WhatsApp"
              value={webhookTemplateName}
              onChange={(event) => setWebhookTemplateName(event.target.value)}
              fullWidth
              required
              helperText="La plantilla debe usar placeholders nombrados para campañas webhook."
            />

            <TextField
              select
              label="Voz TTS"
              value={webhookVoiceModelSelection}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => setWebhookVoiceModelSelection(event.target.value)}
              fullWidth
              helperText="Se aplica a contactos nuevos y existentes. La voz enviada por HubSpot tiene prioridad. Predeterminada conserva la voz de contactos existentes."
            >
              {VOICE_MODEL_OPTIONS.map((option) => (
                <MenuItem key={option.value || 'default'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {renderProjectSelector(
              webhookSelectedProject,
              setWebhookSelectedProject,
              webhookSubmitting
            )}

            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={webhookSkipIdentityValidation}
                    onChange={(event) => setWebhookSkipIdentityValidation(event.target.checked)}
                    color="primary"
                  />
                )}
                label="Omitir validación de identidad"
              />
              <Typography variant="body2" color="text.secondary">
                Solo aplica si el número ya existe en HubSpot o en clientes locales. Si no existe, el contacto seguirá el flujo de registro actual.
              </Typography>
            </Box>

            <Box>
              <FormControlLabel
                control={(
                  <Switch
                    checked={webhookScheduleReengagement}
                    onChange={(event) => setWebhookScheduleReengagement(event.target.checked)}
                    color="primary"
                  />
                )}
                label="Programar reenganche"
              />
              <TextField
                label="Primer reenganche (días hábiles)"
                type="number"
                value={webhookReengagementDelayBusinessDays}
                onChange={(event) => setWebhookReengagementDelayBusinessDays(normalizeReengagementDelayInput(event.target.value))}
                inputProps={{ min: MIN_REENGAGEMENT_DELAY_DAYS, step: 'any' }}
                sx={{ maxWidth: 260, display: 'block', mt: 1 }}
                disabled={!webhookScheduleReengagement}
                helperText={
                  webhookScheduleReengagement
                    ? 'Enteros usan días hábiles; decimales usan tiempo exacto para pruebas.'
                    : 'No se programará reenganche para esta campaña.'
                }
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
                <TextField
                  label="Reenganches sin respuesta"
                  type="number"
                  value={webhookNoAnswerReengagementAttempts}
                  onChange={(event) => setWebhookNoAnswerReengagementAttempts(normalizeNoAnswerAttemptsInput(event.target.value))}
                  inputProps={{ min: 1, max: 10, step: 1 }}
                  disabled={!webhookScheduleReengagement}
                  helperText="Incluye el intento final."
                />
                <TextField
                  label="Incremento por intento (días hábiles)"
                  type="number"
                  value={webhookNoAnswerReengagementIncrementBusinessDays}
                  onChange={(event) => setWebhookNoAnswerReengagementIncrementBusinessDays(normalizeReengagementDelayInput(event.target.value))}
                  inputProps={{ min: MIN_REENGAGEMENT_DELAY_DAYS, step: 'any' }}
                  disabled={!webhookScheduleReengagement}
                />
              </Stack>
            </Box>

            <TextField
              label="Variables adicionales del webhook"
              value={webhookRuntimeVariablesInput}
              onChange={(event) => setWebhookRuntimeVariablesInput(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              helperText="Escribe una variable por línea o separadas por coma. Ejemplo: proyectos, proyecto_interes."
            />

            <TextField
              label="HubSpot signature secret (opcional)"
              value={webhookSignatureSecret}
              onChange={(event) => setWebhookSignatureSecret(event.target.value)}
              fullWidth
              helperText="Si lo configuras, además del bearer token validaremos la firma de HubSpot cuando venga en el request."
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Media de Encabezado
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Puedes usar la predeterminada o subir imagen/video para todos los disparos de este webhook.
              </Typography>

              {webhookHeaderMediaType === 'video' && webhookHeaderImageUrl ? (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Vista previa video (encabezado): {webhookHeaderImageUrl}
                  </Typography>
                  <Box
                    component="video"
                    src={webhookHeaderImageUrl}
                    controls
                    sx={{
                      width: '100%',
                      maxWidth: 460,
                      maxHeight: 220,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                </Box>
              ) : (
                <Box
                  component="img"
                  src={effectiveWebhookPreviewUrl}
                  alt="Vista previa webhook"
                  sx={{
                    width: '100%',
                    maxWidth: 460,
                    maxHeight: 220,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 1.5,
                  }}
                />
              )}

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button component="label" variant="outlined" disabled={webhookUploadingImage || webhookSubmitting}>
                  {webhookUploadingImage ? 'Cargando media...' : 'Subir media'}
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp,video/mp4"
                    onChange={handleWebhookImageSelection}
                  />
                </Button>
                {webhookUploadingImage && <CircularProgress size={20} />}
                {webhookHeaderImageUrl && (
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => {
                      setWebhookHeaderImageUrl(null);
                      setWebhookHeaderImageMediaId(null);
                      setWebhookHeaderMediaType(null);
                    }}
                    disabled={webhookUploadingImage || webhookSubmitting}
                  >
                    Usar predeterminada
                  </Button>
                )}
              </Stack>
            </Box>

            <Alert severity="info">
              Campos reservados por disparo: <code>number</code>, <code>nombre_cliente</code>, <code>hubspot_deal_id</code>,
              y opcionales <code>hubspot_contact_id</code>, <code>apellido_cliente</code>, <code>email</code>,
              <code>voice_model_selection</code>.
            </Alert>

            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                onClick={handleCreateWebhookCampaign}
                disabled={webhookSubmitting || webhookUploadingImage}
              >
                {webhookSubmitting ? 'Creando webhook...' : 'Crear webhook'}
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default InitiateCampaign;
